import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { getCmsUrlValue, getCmsValue } from '../lib/siteContent';
import { isPublicShopEnabled } from '../lib/config';

const LOGO = '/assets/smokehouse/harryda-bbq-logo-400.webp';

const cmsBoolean = (siteContent, field, fallback) => {
  const value = getCmsValue(siteContent, 'global_nav', field);
  if (typeof value === 'boolean') return value;
  const normalized = String(value || '').trim().toLowerCase();
  if (['true', '1', 'yes', 'ja', 'on'].includes(normalized)) return true;
  if (['false', '0', 'no', 'nej', 'off'].includes(normalized)) return false;
  return fallback;
};

const scrollToTopAfterNavigation = () => {
  window.setTimeout(() => window.scrollTo({ top: 0, left: 0, behavior: 'auto' }), 0);
};

/**
 * Gemensam huvudmeny för hela publika sajten. Länkar och etiketter kommer
 * från CMS-sektionen global_nav så att alla sidor visar samma meny.
 */
export default function SmokeNav({ siteContent = {} }) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const closeMenu = () => setMenuOpen(false);

  const nav = (field) => getCmsValue(siteContent, 'global_nav', field);
  const navUrl = (field) => getCmsUrlValue(siteContent, 'global_nav', field);
  const productsVisible = cmsBoolean(siteContent, 'products_visible', true);
  const orderVisible = cmsBoolean(siteContent, 'order_visible', true);
  const publicShopEnabled = isPublicShopEnabled();
  const ctaUrl = navUrl('cta_url');
  const showCta = pathname.replace(/\/+$/, '') !== (ctaUrl.split('#')[0].replace(/\/+$/, '') || '');
  const currentPath = pathname.replace(/\/+$/, '') || '/';
  const ariaCurrent = (url) => ((url.split('#')[0].replace(/\/+$/, '') || '/') === currentPath ? 'page' : undefined);

  useEffect(() => {
    if (!menuOpen) return undefined;
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') setMenuOpen(false);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [menuOpen]);

  useEffect(() => { setMenuOpen(false); }, [pathname]);

  // Menyn är fixed; exponera dess höjd så innehållet kan börja under den.
  useEffect(() => {
    const nav = document.querySelector('.smoke-nav');
    if (!nav || typeof ResizeObserver === 'undefined') return undefined;
    const update = () => {
      document.documentElement.style.setProperty('--smoke-nav-height', `${nav.offsetHeight}px`);
    };
    update();
    const observer = new ResizeObserver(update);
    observer.observe(nav);
    return () => observer.disconnect();
  }, []);

  return (
    <nav className="smoke-nav" aria-label="Huvudmeny">
      <Link className="smoke-brand" to={navUrl('home_url')} onClick={closeMenu}>
        <img src={LOGO} alt={nav('brand_alt')} width="38" height="38" />
        <span>{nav('brand_label')}</span>
      </Link>
      <div
        id="smoke-mobile-nav"
        className={`smoke-nav-links ${menuOpen ? 'open' : ''}`.trim()}
        aria-label="Huvudnavigation"
      >
        <Link to={navUrl('home_url')} onClick={closeMenu} aria-current={ariaCurrent(navUrl('home_url'))}>{nav('home_label')}</Link>
        <Link to={navUrl('catering_url')} onClick={closeMenu} aria-current={ariaCurrent(navUrl('catering_url'))}>{nav('catering_label')}</Link>
        {productsVisible && <Link to={navUrl('products_url')} onClick={closeMenu} aria-current={ariaCurrent(navUrl('products_url'))}>{nav('products_label')}</Link>}
        {orderVisible && <Link to={navUrl('order_url')} onClick={closeMenu} aria-current={ariaCurrent(navUrl('order_url'))}>{nav('order_label')}</Link>}
        {publicShopEnabled && <Link to={navUrl('shop_url')} onClick={closeMenu}>{nav('shop_label')}</Link>}
        <a href={navUrl('values_url')} onClick={closeMenu}>{nav('values_label')}</a>
        <Link to={navUrl('contact_url')} onClick={closeMenu} aria-current={ariaCurrent(navUrl('contact_url'))}>{nav('contact_label')}</Link>
      </div>
      {showCta && (
        <button
          className="smoke-cta"
          onClick={() => {
            closeMenu();
            navigate(ctaUrl);
            scrollToTopAfterNavigation();
          }}
          type="button"
        >
          {nav('cta_label')}
        </button>
      )}
      <button
        type="button"
        className="smoke-menu-toggle"
        aria-controls="smoke-mobile-nav"
        aria-expanded={menuOpen}
        aria-label={menuOpen ? 'Stäng meny' : 'Öppna meny'}
        onClick={() => setMenuOpen((open) => !open)}
      >
        {menuOpen ? <X size={20} aria-hidden="true" /> : <Menu size={20} aria-hidden="true" />}
      </button>
    </nav>
  );
}
